import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Filter, Star, ChevronDown, Award, Users, Hammer } from "lucide-react";
import { PROJECTS, REVIEWS, BLOG, FAQS, BUSINESS, IMAGES, SERVICES } from "@/data/content";
import { SectionHeading, CTA, FinalCTA, ReviewCard, Stars, TrustBar } from "@/components/Shared";
import { CertificationsSection } from "@/pages/FreeRoofInspectionPage";
import { SEO, faqSchema, articleSchema, reviewsSchema, breadcrumbSchema } from "@/components/SEO";
import { HScroll, HScrollItem, ShinglePattern, RoofAccent, DarkHeroAccent, HouseIcon } from "@/components/RoofAccents";

export function AboutPage() {
  return (
    <div>
      <SEO
        title="About Goral Construction - Family-Owned Roofers in Waterford, MI"
        description="Family-owned roofing contractor in Waterford, MI since 2010. Meet Maciej Ogonowski & the crew. Licensed, insured, Master Certified. Serving Oakland County."
        path="/about"
      />
      <section className="bg-bg-dark text-white py-20 md:py-28 relative overflow-hidden group">
        <DarkHeroAccent tone="house" />
        <div className="container-x grid md:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="overline text-brand">About us</div>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3 leading-[1.02]">A Michigan family, one honest roof at a time.</h1>
            <p className="text-white/85 text-lg mt-5 leading-relaxed">Founded in {BUSINESS.founded} by Maciej Ogonowski, owner and lead installer with <strong className="text-white">28 years of Michigan roofing experience</strong> since 1998. What started as one man on a ladder has grown into Oakland County's trusted family-owned roofing company - same hands-on craftsmanship, same neighbor-to-neighbor service.</p>
          </div>
          <div className="aspect-[4/5] overflow-hidden"><img src={IMAGES.team} alt="Maciej Ogonowski, owner of Goral Construction LLC, on a finished roof in Michigan" className="w-full h-full object-cover" /></div>
        </div>
      </section>
      <TrustBar />
      <section className="py-20">
        <div className="container-x grid md:grid-cols-3 gap-10 items-start">
          <div className="md:col-span-1">
            <SectionHeading overline="Our story" title="Honest work, built on relationships." />
            <img src={IMAGES.truckHome} alt="Goral Construction branded truck on a job site in Oakland County" className="mt-8 w-full aspect-[4/3] object-cover border border-ink-line" />
          </div>
          <div className="md:col-span-2 space-y-5 text-ink-soft leading-relaxed text-lg">
            <p>Maciej Ogonowski has been working on Michigan roofs since <strong className="text-ink">1998</strong>. After more than a decade installing for other crews -- through every kind of weather Oakland County throws at a roof -- he started <strong className="text-ink">Goral Construction LLC</strong> in 2010 to do the job his way: no shortcuts, no high-pressure sales, no excuses.</p>
            <p>The promise was simple. Treat every customer's home like his own. Show up when he says he will. Give straight answers, even when the answer is "your roof is fine, wait two years." Clean up every nail before leaving the driveway.</p>
            <p>Fifteen years and over a thousand roofs later, Maciej still runs the company personally. He still answers the phone. He still walks every finished roof before signing it off. The crew he's trained lives in the same towns where they work -- so when you hire Goral Construction, you're not hiring a sales rep with a clipboard. You're hiring a neighbor with 28 years of Michigan roofing under his belt.</p>
            <p><Link to="/about/maciej-ogonowski" className="text-brand font-bold underline hover:no-underline" data-testid="link-maciej-profile">Read Maciej's full owner profile, certifications and credentials →</Link></p>
          </div>
        </div>
      </section>
      <section className="py-20 bg-bg-soft">
        <div className="container-x">
          <SectionHeading overline="What we stand for" title="Three promises we make on every job." />
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-ink-line border border-ink-line">
            {[
              { i: Users, t: "Family, not franchise", d: "We're not a national chain. We're a local family business that answers to our reputation in Oakland County.", v: "skyline" },
              { i: Hammer, t: "Craftsmanship over shortcuts", d: "Every install is to manufacturer spec. Every flashing is sealed right. No exceptions, no rushed corners.", v: "shingles" },
              { i: Award, t: "Honest pricing", d: "Written, itemized estimates. No hidden fees. No high-pressure sales. If your roof has 5 years left, we'll say so.", v: "window" },
            ].map(v => (
              <div key={v.t} className="group relative bg-white p-8 overflow-hidden hover:bg-bg-soft transition-colors">
                <RoofAccent variant={v.v} position="bottom-right" size="lg" intensity="soft" />
                <v.i className="w-8 h-8 text-brand relative" /><h3 className="font-heading text-xl font-extrabold mt-4 relative">{v.t}</h3><p className="text-ink-soft mt-2 leading-relaxed relative">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container-x">
          <SectionHeading overline="Credentials" title="Licensed, insured, and certified." />
          <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl">
            {BUSINESS.certifications.concat(["GAF Factory-Certified Installer", "CertainTeed Quality Master"]).map(c => (
              <div key={c} className="flex items-center gap-3 p-4 bg-bg-soft border-l-4 border-brand"><Award className="w-5 h-5 text-brand" /><span className="font-semibold">{c}</span></div>
            ))}
          </div>
        </div>
      </section>
      <CertificationsSection />
      <FinalCTA />
    </div>
  );
}

export function OurWorkPage() {
  const [filter, setFilter] = React.useState("All");
  const types = ["All", ...Array.from(new Set(PROJECTS.map(p => p.type)))];
  const filtered = filter === "All" ? PROJECTS : PROJECTS.filter(p => p.type === filter);
  return (
    <div>
      <SEO
        title="Our Roofing Projects in Oakland County, MI"
        description="Browse recent roofing, siding, gutter, and storm damage projects completed by Goral Construction across Waterford, Clarkston, Troy, and all of Oakland County."
        path="/our-work"
      />
      <section className="bg-bg-dark text-white py-20 md:py-28 relative overflow-hidden group">
        <DarkHeroAccent tone="shingles" />
        <div className="container-x relative">
          <div className="overline text-brand">Our work</div>
          <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">Real Michigan roofs. Real Oakland County homes.</h1>
          <p className="text-white/80 text-lg max-w-2xl mt-5 leading-relaxed">Browse recent installs, repairs, and insurance-claim work from across the county.</p>
        </div>
      </section>
      <section className="py-16">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-10">
            {types.map(t => (
              <button key={t} onClick={() => setFilter(t)} className={`px-4 py-2 text-sm font-semibold border rounded-sm transition-colors ${filter === t ? "bg-ink text-white border-ink" : "bg-white text-ink border-ink-line hover:border-ink"}`} data-testid={`filter-${t.replace(/\s/g,'-')}`}>{t}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
            {filtered.map(p => (
              <div key={p.id} className="group">
                <div className="aspect-[4/5] overflow-hidden bg-bg-soft"><img src={p.image} loading="lazy" alt={`${p.type} in ${p.city}, MI`} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="mt-3 md:mt-4">
                  <div className="overline text-[10px] md:text-xs">{p.city}, MI · {p.date}</div>
                  <div className="font-heading text-base md:text-xl font-bold mt-1">{p.type}</div>
                  <div className="text-ink-soft text-xs md:text-sm mt-1">{p.material}</div>
                  <div className="hidden md:block text-ink-soft text-sm mt-2 leading-relaxed">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FinalCTA />
    </div>
  );
}

export function ReviewsPage() {
  return (
    <div>
      <SEO
        title="Customer Reviews - 5.0 on Angi | Goral Construction"
        description={`${BUSINESS.rating.count}+ verified reviews, 5.0 stars. See what Oakland County homeowners say about Goral Construction's roofing, siding, and gutter work.`}
        path="/reviews"
        schema={reviewsSchema(REVIEWS)}
      />
      <section className="bg-bg-dark text-white py-20 md:py-28 relative overflow-hidden group">
        <DarkHeroAccent tone="ridgeline" />
        <div className="container-x relative">
          <div className="overline text-brand">Reviews</div>
          <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">5.0 stars. Earned the hard way.</h1>
          <div className="mt-6 flex flex-wrap items-center gap-6 text-white/90"><Stars value={5} /><div className="font-heading text-3xl font-bold">5.0</div><div className="text-white/70">· {BUSINESS.rating.count} verified reviews on Angi. Read more on Google & Facebook.</div></div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={BUSINESS.social.angi} target="_blank" rel="noopener noreferrer" className="btn-inverted">Read on Angi →</a>
            <a href={BUSINESS.social.google} target="_blank" rel="noopener noreferrer" className="btn-inverted">Read on Google Maps →</a>
            <a href={BUSINESS.social.facebook} target="_blank" rel="noopener noreferrer" className="btn-inverted">Read on Facebook →</a>
          </div>
        </div>
      </section>
      <section className="py-20"><div className="container-x"><HScroll className="md:grid-cols-3" testId="reviews-page-scroller">{REVIEWS.map(r => <HScrollItem key={r.name}><ReviewCard r={r} /></HScrollItem>)}</HScroll></div></section>
      <section className="py-16 bg-bg-soft"><div className="container-x text-center"><SectionHeading overline="Leave a review" title="Worked with us? Tell the world." align="center" /><div className="mt-6 flex flex-wrap gap-3 justify-center"><a href={BUSINESS.social.google} target="_blank" rel="noopener noreferrer" className="btn-secondary">Review on Google</a><a href={BUSINESS.social.angi} target="_blank" rel="noopener noreferrer" className="btn-secondary">Review on Angi</a><a href={BUSINESS.social.facebook} target="_blank" rel="noopener noreferrer" className="btn-secondary">Review on Facebook</a></div></div></section>
      <FinalCTA />
    </div>
  );
}

export function BlogHubPage() {
  return (
    <div>
      <SEO
        title="Roofing Blog - Michigan Tips, Costs & Advice"
        description="Roofing cost guides, insurance claim how-tos, ice dam prevention, and material comparisons - written by Michigan roofers for Michigan homeowners."
        path="/blog"
      />
      <section className="bg-bg-dark text-white py-20 md:py-28 relative overflow-hidden group"><DarkHeroAccent tone="skyline" /><div className="container-x relative"><div className="overline text-brand">Blog</div><h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">Roofing tips, costs, and Michigan-specific advice.</h1><p className="text-white/80 text-lg max-w-2xl mt-5 leading-relaxed">Written by the crew, for homeowners. No fluff, no filler - just the stuff we wish everyone knew before their first roof call.</p></div></section>
      <section className="py-20 md:py-24 relative">
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
          <ShinglePattern className="w-full h-full" color="#0A0A0A" />
        </div>
        <div className="container-x relative grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">{BLOG.map((b, i) => (
          <Link key={b.slug} to={`/blog/${b.slug}`} className="group block relative" data-testid={`blog-card-${b.slug}`}>
            <div className="relative aspect-[16/10] overflow-hidden bg-bg-soft">
              <img src={b.hero} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1 text-[10px] font-bold tracking-[0.18em] uppercase text-ink">{b.category}</div>
              <RoofAccent variant={["gable", "skyline", "house", "ridgeline"][i % 4]} position="bottom-right" size="sm" intensity="medium" hoverOnly={true} onDark={false} />
            </div>
            <div className="mt-5 pl-1">
              <div className="overline text-ink-soft">{b.read} · {b.date}</div>
              <div className="font-heading text-2xl font-extrabold mt-2 leading-tight group-hover:text-brand transition-colors">{b.title}</div>
              <p className="text-ink-soft mt-3 leading-relaxed text-[15px]">{b.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ink group-hover:text-brand transition-colors">Read article <span className="transition-transform group-hover:translate-x-1">→</span></div>
            </div>
          </Link>
        ))}</div>
      </section>
      <FinalCTA />
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG.find(b => b.slug === slug);
  if (!post) return <div className="container-x py-32"><h1 className="font-heading text-4xl font-bold">Post not found</h1><Link to="/blog" className="btn-primary mt-6">Back to blog</Link></div>;
  const related = (post.related ? post.related.map(s => BLOG.find(b => b.slug === s)).filter(Boolean) : BLOG.filter(b => b.slug !== slug)).slice(0, 3);
  const hasSections = Array.isArray(post.sections) && post.sections.length > 0;

  return (
    <div>
      <SEO
        title={post.seoTitle || post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.hero}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            articleSchema({ title: post.title, description: post.excerpt, path: `/blog/${post.slug}`, image: post.hero, date: post.date, read: post.read }),
            ...(post.faqs && post.faqs.length ? [faqSchema(post.faqs)] : []),
            breadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Blog", path: "/blog" },
              { name: post.title.length > 60 ? post.title.slice(0, 57) + "..." : post.title, path: `/blog/${post.slug}` },
            ]),
          ],
        }}
      />

      {/* Hero with roof skyline accent */}
      <section className="bg-bg-dark text-white py-24 md:py-32 relative overflow-hidden">
        <DarkHeroAccent tone="house" />
        <div className="container-x relative">
          <Link to="/blog" className="overline text-brand hover:text-white inline-flex items-center gap-1 transition-colors">← Blog</Link>
          <div className="overline text-white/60 mt-8 flex flex-wrap gap-3">
            <span>{post.category}</span><span className="opacity-50">·</span><span>{post.date}</span><span className="opacity-50">·</span><span>{post.read}</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight mt-4 max-w-4xl leading-[1.05]">{post.title}</h1>
          <p className="text-white/75 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {/* Article body */}
      <section className="py-16 md:py-24 relative">
        <div className="absolute inset-0 pointer-events-none opacity-[0.025]"><ShinglePattern className="w-full h-full" color="#0A0A0A" /></div>
        <div className="container-x relative max-w-3xl">
          <div className="aspect-[16/9] overflow-hidden mb-12 relative">
            <img src={post.hero} alt={post.title} className="w-full h-full object-cover" />
            <RoofAccent variant="ridgeline" position="bottom-left" size="lg" intensity="medium" hoverOnly={false} onDark={false} />
          </div>

          <article className="prose-roofing text-ink leading-[1.75]">
            {/* LEAD (TL;DR) */}
            {(post.lead || hasSections === false) && (
              <p className="lead text-xl md:text-2xl font-medium text-ink leading-relaxed first-letter:font-heading first-letter:text-7xl first-letter:font-extrabold first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.85] first-letter:text-brand mb-10" data-testid="blog-lead">
                {renderInline(post.lead || (post.body && post.body[0]) || "")}
              </p>
            )}

            {/* IN THIS ARTICLE - auto-TOC */}
            {hasSections && (
              <nav className="not-prose bg-bg-soft border-l-4 border-brand p-5 my-10" aria-label="In this article" data-testid="blog-toc">
                <div className="overline text-brand mb-3">In this article</div>
                <ul className="space-y-1.5">
                  {post.sections.map((s) => (
                    <li key={s.id}><a href={`#${s.id}`} className="text-ink font-semibold hover:text-brand underline-offset-4 hover:underline text-[15px]">{s.h2}</a></li>
                  ))}
                  {post.bottomLine && <li><a href="#bottom-line" className="text-ink font-semibold hover:text-brand underline-offset-4 hover:underline text-[15px]">Bottom line</a></li>}
                  {post.faqs && post.faqs.length > 0 && <li><a href="#faqs" className="text-ink font-semibold hover:text-brand underline-offset-4 hover:underline text-[15px]">Frequently asked questions</a></li>}
                </ul>
              </nav>
            )}

            {/* NEW STRUCTURED SECTIONS */}
            {hasSections && post.sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24 mb-12">
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mt-12 mb-5">{s.h2}</h2>
                {s.body.map((node, i) => <BlogNode key={i} node={node} />)}
              </section>
            ))}

            {/* BACKWARDS COMPAT: old short blogs with flat body[] */}
            {!hasSections && post.body && (
              <div className="space-y-7 text-lg">
                {post.body.slice(post.lead ? 0 : 1).map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                ))}
              </div>
            )}

            {/* BOTTOM LINE */}
            {post.bottomLine && (
              <section id="bottom-line" className="scroll-mt-24 mt-14 bg-ink text-white p-7 md:p-9 relative overflow-hidden" data-testid="blog-bottom-line">
                <div className="absolute inset-0 opacity-10 pointer-events-none"><ShinglePattern className="w-full h-full" color="#FFFFFF" /></div>
                <div className="relative">
                  <div className="overline text-brand mb-3">Bottom line</div>
                  <p className="text-[17px] md:text-lg leading-relaxed bottom-line">{renderInline(post.bottomLine)}</p>
                </div>
              </section>
            )}

            {/* FAQS */}
            {post.faqs && post.faqs.length > 0 && (
              <section id="faqs" className="scroll-mt-24 mt-14">
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mb-6">Frequently asked questions about {post.category.toLowerCase()}</h2>
                <div className="border-t border-ink-line">
                  {post.faqs.map((f, i) => (
                    <details key={i} open className="group border-b border-ink-line py-5">
                      <summary className="flex items-start justify-between gap-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                        <h3 className="font-heading text-lg md:text-xl font-bold leading-snug text-ink group-open:text-brand transition-colors">{f.q}</h3>
                        <ChevronDown className="w-5 h-5 flex-shrink-0 mt-1 text-ink-soft transition-transform group-open:rotate-180" />
                      </summary>
                      <p className="mt-3 text-ink-soft leading-relaxed text-[16px]">{renderInline(f.a)}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </article>

          {/* Decorative roof divider before author */}
          <div className="mt-16 mb-10 flex items-center gap-4">
            <div className="flex-1 h-px bg-ink-line" />
            <HouseIcon className="w-8 h-8 text-brand opacity-60" />
            <div className="flex-1 h-px bg-ink-line" />
          </div>

          {/* Author card */}
          <div className="bg-bg-soft border border-ink-line p-6 md:p-8 flex items-start gap-5">
            <Link to="/about/maciej-ogonowski" className="w-14 h-14 rounded-full bg-ink text-brand flex items-center justify-center font-heading font-extrabold text-xl flex-shrink-0 hover:bg-brand hover:text-white transition-colors" data-testid="blog-author-avatar">MO</Link>
            <div>
              <div className="overline text-brand">Written by</div>
              <Link to="/about/maciej-ogonowski" className="font-heading text-xl font-extrabold mt-1 block hover:text-brand transition-colors" data-testid="blog-author-link">Maciej Ogonowski</Link>
              <div className="text-sm text-ink-soft mt-1">Owner & Master Shingle Applicator (Wizard Level) · Goral Construction LLC</div>
              <div className="text-sm text-ink-soft mt-3 leading-relaxed">28+ years on Michigan roofs. Family-owned, licensed, insured. We install only what we'd put on our own homes - same crew, same warranty, every job. <Link to="/about/maciej-ogonowski" className="text-brand font-semibold underline hover:no-underline">Read full profile →</Link></div>
            </div>
          </div>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-20 bg-bg-soft border-t border-ink-line">
          <div className="container-x">
            <div className="overline text-brand">Keep reading</div>
            <div className="font-heading text-3xl md:text-4xl font-extrabold mt-2">More from the Goral blog</div>
            <div className="mt-10 grid md:grid-cols-3 gap-8">
              {related.map((b, i) => (
                <Link key={b.slug} to={`/blog/${b.slug}`} className="group block">
                  <div className="relative aspect-[16/10] overflow-hidden bg-white">
                    <img src={b.hero} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <RoofAccent variant={["gable", "skyline", "house"][i % 3]} position="bottom-right" size="sm" intensity="soft" hoverOnly={true} onDark={false} />
                  </div>
                  <div className="mt-4">
                    <div className="overline">{b.category} · {b.read}</div>
                    <div className="font-heading text-lg font-bold mt-2 group-hover:text-brand transition-colors leading-snug">{b.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FinalCTA />
    </div>
  );
}

// Render a single structured-content node inside a blog section.
function BlogNode({ node }) {
  if (!node) return null;
  if (node.type === "p") return <p className="text-lg leading-[1.8] mb-5">{renderInline(node.text)}</p>;
  if (node.type === "h3") return <h3 className="font-heading text-xl md:text-2xl font-extrabold tracking-tight mt-8 mb-3">{node.text}</h3>;
  if (node.type === "list") return (
    <ul className="list-none space-y-2 mb-5 ml-0">
      {node.items.map((it, i) => (
        <li key={i} className="text-lg leading-[1.7] pl-6 relative">
          <span className="absolute left-0 top-[10px] w-2 h-2 bg-brand" />
          {renderInline(it)}
        </li>
      ))}
    </ul>
  );
  if (node.type === "ol") return (
    <ol className="space-y-2 mb-5 ml-0 list-decimal pl-6">
      {node.items.map((it, i) => <li key={i} className="text-lg leading-[1.7] marker:text-brand marker:font-extrabold">{renderInline(it)}</li>)}
    </ol>
  );
  if (node.type === "table") return (
    <div className="overflow-x-auto my-7 -mx-4 md:mx-0">
      <table className="w-full text-[15px] border-collapse">
        <thead><tr className="bg-ink text-white">{node.headers.map((h, i) => <th key={i} className="px-4 py-3 text-left font-heading font-bold uppercase tracking-wider text-xs">{h}</th>)}</tr></thead>
        <tbody>
          {node.rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 ? "bg-bg-soft" : "bg-white"}>
              {row.map((cell, ci) => <td key={ci} className="px-4 py-3 border-b border-ink-line align-top leading-snug">{renderInline(String(cell))}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  if (node.type === "callout") return (
    <aside className="border-l-4 border-brand bg-brand/5 p-5 my-6 text-[16px] leading-relaxed">
      {node.title && <div className="overline text-brand mb-1">{node.title}</div>}
      {renderInline(node.text)}
    </aside>
  );
  return null;
}

// Simple inline-markdown to JSX: supports [text](url) for internal links and **bold**.
// We render answers as static visible text so crawlers and LLMs index them without JS.
function renderInline(text) {
  if (!text) return null;
  // Split on link pattern first
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ type: "link", label: m[1], url: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => {
    if (typeof p === "string") {
      // bold inside
      const html = p.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
    }
    if (p.url.startsWith("/")) {
      return <Link key={i} to={p.url} className="text-brand font-semibold underline hover:no-underline">{p.label}</Link>;
    }
    return <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="text-brand font-semibold underline hover:no-underline">{p.label}</a>;
  });
}

function slugifyCategory(c) {
  return c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function FAQPage() {
  const categories = Array.from(new Set(FAQS.map(f => f.category)));
  return (
    <div>
      <SEO
        title="Roofing FAQs - Oakland County, MI | Goral Construction"
        description="Detailed answers on roof cost, insurance claims, materials, process, warranties, service area, emergency repair, and seasonal maintenance for Oakland County homeowners."
        path="/faq"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            faqSchema(FAQS),
            breadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "FAQ", path: "/faq" },
            ]),
          ],
        }}
      />

      <section className="bg-bg-dark text-white py-20 md:py-24 relative overflow-hidden group">
        <DarkHeroAccent tone="ridgeline" />
        <div className="container-x relative">
          <div className="overline text-brand">FAQ · Oakland County, MI</div>
          <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">Frequently asked questions.</h1>
          <p className="text-white/80 text-lg mt-5 max-w-2xl leading-relaxed">Real, detailed answers from the crew. Everything below is visible on the page - no JavaScript required, no tricks. If your question is not here, call <a href={`tel:${BUSINESS.phoneTel}`} className="text-brand underline">{BUSINESS.phone}</a>.</p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container-x grid lg:grid-cols-[260px_minmax(0,1fr)] gap-10">
          {/* Quick-links sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start" aria-label="FAQ categories">
            <div className="bg-bg-soft border border-ink-line p-5">
              <div className="overline text-brand mb-3">Quick links</div>
              <nav className="space-y-2" data-testid="faq-quicklinks">
                {categories.map((c) => (
                  <a key={c} href={`#${slugifyCategory(c)}`} className="block text-sm font-semibold text-ink hover:text-brand transition-colors py-1">
                    {c} <span className="text-ink-soft font-normal">({FAQS.filter(f => f.category === c).length})</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content - static H2 / H3 / p, content visible by default inside <details open> */}
          <div className="max-w-3xl">
            {categories.map((cat) => {
              const items = FAQS.filter(f => f.category === cat);
              const id = slugifyCategory(cat);
              return (
                <section key={cat} id={id} className="mb-14 scroll-mt-24">
                  <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">{cat}</h2>
                  <div className="mt-6 border-t border-ink-line">
                    {items.map((f, i) => (
                      <details key={i} open className="group border-b border-ink-line py-5" data-testid={`faq-${id}-${i}`}>
                        <summary className="flex items-start justify-between gap-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                          <h3 className="font-heading text-lg md:text-xl font-bold leading-snug text-ink group-open:text-brand transition-colors">{f.q}</h3>
                          <ChevronDown className="w-5 h-5 flex-shrink-0 mt-1 text-ink-soft transition-transform group-open:rotate-180" />
                        </summary>
                        <p className="mt-3 text-ink-soft leading-relaxed text-[16px]">{renderInline(f.a)}</p>
                      </details>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

export function FinancingPage() {
  return (
    <div>
      <SEO
        title="Roof Financing Options in Oakland County, MI"
        description="Flexible roof financing - 0% promotional APR and long-term fixed-rate loans for qualified homeowners. Quick approval, trusted lending partners."
        path="/financing"
      />
      <section className="bg-bg-dark text-white py-20 relative overflow-hidden group"><DarkHeroAccent tone="skyline" /><div className="container-x relative"><div className="overline text-brand">Financing</div><h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">Flexible roof financing. Zero stress.</h1><p className="text-white/80 text-lg max-w-2xl mt-5 leading-relaxed">We partner with trusted lenders to offer promotional 0% APR, long-term fixed-rate loans, and same-as-cash options for qualified homeowners.</p></div></section>
      <section className="py-20"><div className="container-x grid md:grid-cols-3 gap-px bg-ink-line border border-ink-line">{[{ t: "0% Promo APR", d: "12-18 month same-as-cash options for qualified applicants.", v: "gable" }, { t: "Long-term Fixed", d: "5-15 year fixed-rate loans with competitive APRs.", v: "door" }, { t: "Quick Approval", d: "Most applications get a decision in under 60 seconds.", v: "vane" }].map(o => (<div key={o.t} className="group relative bg-white p-8 overflow-hidden hover:bg-bg-soft transition-colors"><RoofAccent variant={o.v} position="bottom-right" size="lg" intensity="soft" /><div className="font-heading text-2xl font-extrabold relative">{o.t}</div><p className="text-ink-soft mt-3 leading-relaxed relative">{o.d}</p></div>))}</div></section>
      <FinalCTA />
    </div>
  );
}

export function LegalPage({ type }) {
  const isPrivacy = type === "privacy";
  return (
    <div>
      <SEO
        title={isPrivacy ? "Privacy Policy" : "Terms of Service"}
        description={isPrivacy ? "How Goral Construction LLC collects, uses, and protects your personal information." : "Terms governing estimates, services, warranty, and liability with Goral Construction LLC."}
        path={isPrivacy ? "/privacy-policy" : "/terms-of-service"}
      >
        <meta name="robots" content="noindex, follow" />
      </SEO>
      <section className="bg-bg-dark text-white py-16"><div className="container-x"><div className="overline text-brand">Legal</div><h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight mt-3">{isPrivacy ? "Privacy Policy" : "Terms of Service"}</h1><p className="text-white/60 text-sm mt-3">Last updated: January 2026</p></div></section>
      <section className="py-16"><div className="container-x max-w-3xl prose prose-zinc space-y-6 text-ink-soft leading-relaxed">
        <p>{isPrivacy
          ? `This Privacy Policy describes how Goral Construction LLC ("we", "us", "our") collects, uses, and protects the personal information you provide through our website, roofing-goral.com.`
          : `These Terms of Service govern your use of roofing-goral.com and any engagement with Goral Construction LLC for services described herein.`}
        </p>
        <h2 className="font-heading text-2xl font-bold text-ink">{isPrivacy ? "Information We Collect" : "Estimates & Quotes"}</h2>
        <p>{isPrivacy
          ? `When you submit a contact or estimate form, we collect your name, email, phone number, and the details you provide about your project. This information is used solely to respond to your inquiry and schedule services.`
          : `All estimates are provided free of charge and are valid for 30 days. Final project cost may vary based on conditions discovered during tear-off or installation, which will be communicated and approved in writing before proceeding.`}</p>
        <h2 className="font-heading text-2xl font-bold text-ink">{isPrivacy ? "How We Use Your Information" : "Warranty"}</h2>
        <p>{isPrivacy
          ? `We use your information to contact you about your project, send quotes, and (if applicable) share project updates. We do not sell your information to third parties.`
          : `Goral Construction LLC warrants workmanship for ten (10) years from date of completion. Materials are covered by the manufacturer's warranty (typically 30-50 years for shingles). Warranty claims must be submitted in writing.`}</p>
        <h2 className="font-heading text-2xl font-bold text-ink">{isPrivacy ? "Cookies" : "Limitation of Liability"}</h2>
        <p>{isPrivacy
          ? `Our site uses essential cookies for functionality and may use analytics cookies to measure traffic. No personal data is sold.`
          : `Our liability is limited to the cost of the services provided. We are not liable for pre-existing structural issues, prior water damage, or conditions disclosed during inspection.`}</p>
        <h2 className="font-heading text-2xl font-bold text-ink">Contact</h2>
        <p>For questions, contact us at <a href={`mailto:${BUSINESS.email}`} className="text-brand font-semibold">{BUSINESS.email}</a> or {BUSINESS.phone}.</p>
      </div></section>
      <FinalCTA />
    </div>
  );
}
